'use client';

import { useEffect, useRef } from 'react';
import { Mesh, Program, Renderer, Triangle } from 'ogl';

// Adapted for CANNECT from React Bits Silk (MIT + Commons Clause):
// https://www.reactbits.dev/backgrounds/silk
// Copyright (c) 2026 David Haz.
// This OGL variant renders at DPR 1, caps animation at 30 FPS and pauses
// while the page is hidden or the background is outside the viewport.

const vertexShader = `
attribute vec2 uv;
attribute vec2 position;
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const fragmentShader = `
precision highp float;

varying vec2 vUv;

uniform float uTime;
uniform vec3 uBaseColor;
uniform vec3 uHighlightColor;
uniform float uSpeed;
uniform float uScale;
uniform float uRotation;
uniform float uNoiseIntensity;

const float e = 2.71828182845904523536;

float noise(vec2 texCoord) {
  vec2 r = e * sin(e * texCoord);
  return fract(r.x * r.y * (1.0 + texCoord.x));
}

vec2 rotateUvs(vec2 uv, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  return mat2(c, -s, s, c) * uv;
}

void main() {
  float randomValue = noise(gl_FragCoord.xy);
  vec2 uv = rotateUvs(vUv * uScale, uRotation);
  vec2 texturePosition = uv * uScale;
  float timeOffset = uSpeed * uTime;

  texturePosition.y += 0.03 * sin(8.0 * texturePosition.x - timeOffset);

  float pattern = 0.6 +
    0.4 * sin(
      5.0 * (
        texturePosition.x +
        texturePosition.y +
        cos(3.0 * texturePosition.x + 5.0 * texturePosition.y) +
        0.02 * timeOffset
      ) +
      sin(20.0 * (
        texturePosition.x +
        texturePosition.y -
        0.1 * timeOffset
      ))
    );

  float tonalMix = smoothstep(0.28, 0.92, pattern);
  float luminance = mix(0.38, 1.05, clamp(pattern, 0.0, 1.0));
  vec3 silkColor = mix(uBaseColor, uHighlightColor, tonalMix);
  vec3 color = silkColor * luminance - vec3(randomValue / 15.0 * uNoiseIntensity);
  gl_FragColor = vec4(color, 1.0);
}
`;

interface LandingSilkProps {
  className?: string;
  baseColor?: string;
  highlightColor?: string;
  onReady?: () => void;
}

function normalizedRgb(hex: string) {
  const clean = hex.replace('#', '');
  return new Float32Array([
    Number.parseInt(clean.slice(0, 2), 16) / 255,
    Number.parseInt(clean.slice(2, 4), 16) / 255,
    Number.parseInt(clean.slice(4, 6), 16) / 255,
  ]);
}

export function LandingSilk({
  className = '',
  baseColor = '#28137F',
  highlightColor = '#4144E6',
  onReady,
}: LandingSilkProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let renderer: Renderer;
    try {
      renderer = new Renderer({
        alpha: false,
        antialias: false,
        dpr: 1,
      });
    } catch {
      onReady?.();
      return;
    }

    const gl = renderer.gl;
    const canvas = gl.canvas as HTMLCanvasElement;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    container.appendChild(canvas);

    const timeUniform = { value: 0 };
    const program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms: {
        uTime: timeUniform,
        uBaseColor: { value: normalizedRgb(baseColor) },
        uHighlightColor: { value: normalizedRgb(highlightColor) },
        uSpeed: { value: 5 },
        uScale: { value: 1 },
        uRotation: { value: 0 },
        uNoiseIntensity: { value: 1.5 },
      },
    });
    const mesh = new Mesh(gl, { geometry: new Triangle(gl), program });

    const render = () => renderer.render({ scene: mesh });
    const resize = () => {
      const bounds = container.getBoundingClientRect();
      renderer.setSize(
        Math.max(1, Math.floor(bounds.width)),
        Math.max(1, Math.floor(bounds.height)),
      );
      render();
    };

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const frameInterval = 1000 / 30;
    let lastFrameAt = 0;
    let frame = 0;
    let running = false;
    let inViewport = true;
    let pageVisible = !document.hidden;
    let ready = false;

    const markReady = () => {
      if (ready) return;
      ready = true;
      onReady?.();
    };

    const tick = (now: number) => {
      if (!running) return;
      if (now - lastFrameAt >= frameInterval) {
        timeUniform.value = now * 0.0001;
        render();
        markReady();
        lastFrameAt = now;
      }
      frame = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (running || !inViewport || !pageVisible) return;
      running = true;
      if (reducedMotion) {
        render();
        markReady();
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };

    const stop = () => {
      running = false;
      window.cancelAnimationFrame(frame);
      frame = 0;
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    resize();

    const intersectionObserver = new IntersectionObserver(([entry]) => {
      inViewport = entry.isIntersecting;
      if (inViewport) start();
      else stop();
    });
    intersectionObserver.observe(container);

    const onVisibilityChange = () => {
      pageVisible = !document.hidden;
      if (pageVisible) start();
      else stop();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    start();

    return () => {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      canvas.remove();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [baseColor, highlightColor, onReady]);

  return (
    <div
      ref={containerRef}
      className={`h-full w-full overflow-hidden bg-[#160a52] ${className}`.trim()}
    />
  );
}
